import { memo, useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';

import Link from '@atlaskit/link';

import { getParams } from 'src/components/pipelines/common/utils';
import {
  CheckRun,
  Checks,
  CheckSuite,
  ChildPipelineStep,
  Deployment,
  HaltedReason,
  Pipeline,
  Repository,
  Step,
} from 'src/components/pipelines/models';
import { useLocation } from 'src/router/hooks';

import {
  DOCS_DEPLOYMENT_CONCURRENCY,
  DOCS_DEPLOYMENTS,
  ACCEPTABLE_USE_POLICY_URL,
  CONTACT_SUPPORT_URL,
} from '../../constants';
import { getAgentProviderDisplayName } from '../../utils/getAgentProviderDisplayName';
import {
  getAgenticPipelinesPageUrl,
  getDeploymentsPageURL,
  getPlansPageURL,
  getStepPageURL,
  getWorkspaceAtlassianIntelligenceSettingsUrl,
} from '../../utils/links';
import ExternalLink from '../ExternalLink';
import {
  HaltedMessageLinks,
  HaltedMessageText,
  HaltedMessageWrapper,
} from '../styled';
import WarningCard from '../WarningCard';

import { messages } from './HaltedMessage.i18n';

export interface Props {
  repository: Repository;
  pipeline: Pipeline;
  steps: (Step | ChildPipelineStep)[];
  checks: Checks;
  deployments: { [key: string]: Deployment };
}

const HaltedMessage = ({
  repository,
  pipeline,
  steps,
  checks,
  deployments,
}: Props) => {
  const location = useLocation();

  const haltedStep = useMemo(() => {
    return steps.filter((step: Step) => step?.isHalted)?.[0];
  }, [steps]);

  const deploymentEnvironment = useMemo(() => {
    if (!haltedStep || haltedStep.isChildStep) return 'the same environment';
    return haltedStep.haltedEnvironmentName || haltedStep.environmentName;
  }, [haltedStep]);

  const getReasonUrl = useCallback(
    (repo: Repository) => {
      const reason = haltedStep
        ? (haltedStep.statusReason as HaltedReason)
        : null;
      return reason
        ? getStepPageURL(
            { uuid: reason.pipeline_uuid },
            reason.step_uuid,
            repo.path
          ) + location.search
        : '';
    },
    [haltedStep, location.search]
  );

  const reasonMessage = useMemo(() => {
    const reason: HaltedReason =
      (haltedStep?.statusReason as HaltedReason) || new HaltedReason();
    if (reason.isDeploymentConcurrencyReason) {
      return (
        <>
          <HaltedMessageText>
            <FormattedMessage
              {...messages.haltedMessageText}
              values={{
                deploymentEnvironment,
                linkOne: chunks => (
                  <ExternalLink href={getReasonUrl(repository)}>
                    {chunks}
                  </ExternalLink>
                ),
                linkTwo: chunks => (
                  <ExternalLink
                    href={getDeploymentsPageURL(repository.path, getParams())}
                  >
                    {chunks}
                  </ExternalLink>
                ),
              }}
            />
          </HaltedMessageText>
          <HaltedMessageText>
            <Link href={DOCS_DEPLOYMENT_CONCURRENCY} target="_blank">
              <FormattedMessage {...messages.haltedMessageLearnMore} />
            </Link>
          </HaltedMessageText>
        </>
      );
    } else if (reason.isUserHasNoDeploymentPermissionsReason) {
      return (
        <>
          <HaltedMessageText>
            <FormattedMessage
              {...messages.userHasNoDeploymentPermissionsReason}
              values={{
                actorDisplayName: reason.actor.displayName,
                deploymentEnvironment,
                linkOne: chunks => (
                  <ExternalLink href={reason.actor.accountUrl}>
                    {chunks}
                  </ExternalLink>
                ),
                linkTwo: chunks => (
                  <ExternalLink
                    href={getDeploymentsPageURL(repository.path, getParams())}
                  >
                    {chunks}
                  </ExternalLink>
                ),
              }}
            />
          </HaltedMessageText>
          <HaltedMessageLinks>
            <Link href={DOCS_DEPLOYMENTS} target="_blank">
              <FormattedMessage {...messages.haltedMessageLearnMore} />
            </Link>
          </HaltedMessageLinks>
        </>
      );
    } else if (reason.isAcceptableUserPolicyBreachedReason) {
      return (
        <>
          <HaltedMessageText>
            <FormattedMessage
              {...messages.acceptableUsePolicyBreachedReasonPartOne}
            />
            <br />
            <FormattedMessage
              {...messages.acceptableUsePolicyBreachedReasonPartTwo}
              values={{
                link: chunks => (
                  <ExternalLink href={CONTACT_SUPPORT_URL}>
                    {chunks}
                  </ExternalLink>
                ),
              }}
            />
          </HaltedMessageText>
          <HaltedMessageLinks>
            <ExternalLink href={ACCEPTABLE_USE_POLICY_URL}>
              <FormattedMessage {...messages.viewAcceptableUsePolicy} />
            </ExternalLink>
          </HaltedMessageLinks>
        </>
      );
    } else if (reason.isBranchHasNoDeploymentPermissionsReason) {
      return (
        <>
          <HaltedMessageText>
            <FormattedMessage
              {...messages.branchHasNoDeploymentPermissionsReason}
              values={{
                deploymentEnvironment,
                link: chunks => (
                  <ExternalLink
                    href={getDeploymentsPageURL(repository.path, getParams())}
                  >
                    {chunks}
                  </ExternalLink>
                ),
                isBranchPipeline: String(pipeline.isBranchPipeline),
                sourceBranch: pipeline.sourceBranch,
              }}
            />
          </HaltedMessageText>
          <HaltedMessageLinks>
            <ExternalLink href={DOCS_DEPLOYMENTS}>
              <FormattedMessage {...messages.haltedMessageLearnMore} />
            </ExternalLink>
          </HaltedMessageLinks>
        </>
      );
    } else if (reason.isAllowanceExceededReason) {
      return (
        <>
          <HaltedMessageText>
            <FormattedMessage {...messages.allowanceExceededReasonPartOne} />
            <br />
            <FormattedMessage {...messages.allowanceExceededReasonPartTwo} />
          </HaltedMessageText>
          <HaltedMessageLinks>
            <ExternalLink href={getPlansPageURL(repository.path)}>
              <FormattedMessage {...messages.viewPlanDetails} />
            </ExternalLink>
          </HaltedMessageLinks>
        </>
      );
    } else if (reason.isDeploymentGateFailedReason) {
      return (
        <HaltedMessageText>
          <FormattedMessage {...messages.deploymentGateFailedReason} />
        </HaltedMessageText>
      );
    } else if (reason.isDeploymentGateChecksRequiredReason) {
      const haltedDeployment: Deployment | undefined = Object.values(
        deployments
      ).find(
        (deployment: any) =>
          !!deployment?.step?.uuid && deployment.step.uuid === haltedStep?.uuid
      );
      const haltedDeploymentCheckSuite: CheckSuite | undefined =
        haltedDeployment &&
        checks.deploymentCheckSuites.find(
          (checkSuite: any) =>
            !!checkSuite &&
            checkSuite['triggerer.uuid'] === haltedDeployment.uuid
        );

      let changeRequestLink: JSX.Element | null = null;
      if (haltedDeploymentCheckSuite) {
        const checkRun: CheckRun | undefined = checks.jsdGatingCheckRuns.find(
          cr => cr?.['check_suite.uuid'] === haltedDeploymentCheckSuite.uuid
        );
        if (checkRun && checkRun['url.href']) {
          changeRequestLink = (
            <a href={checkRun['url.href']} target="_blank" rel="noopener">
              View change request
            </a>
          );
        }
      }

      if (haltedStep?.isManual) {
        return (
          <>
            <HaltedMessageText>
              <FormattedMessage {...messages.manualHaltedStep} />
            </HaltedMessageText>
            {changeRequestLink}
          </>
        );
      }
      return (
        <>
          <HaltedMessageText>
            <FormattedMessage {...messages.automaticalHaltedStep} />
          </HaltedMessageText>
          {changeRequestLink}
        </>
      );
    } else if (reason.isAgenticPipelinesNotEnabledReason) {
      return (
        <>
          <HaltedMessageText>
            <FormattedMessage {...messages.agenticPipelinesNotEnabledReason} />
          </HaltedMessageText>
          <HaltedMessageLinks>
            <ExternalLink
              href={getWorkspaceAtlassianIntelligenceSettingsUrl(
                repository.getWorkplaceSlug()
              )}
            >
              <FormattedMessage {...messages.enableAgenticPipelines} />
            </ExternalLink>
          </HaltedMessageLinks>
        </>
      );
    } else if (reason.isAgenticQuotaExceededReason) {
      return (
        <HaltedMessageText>
          <FormattedMessage {...messages.agenticQuotaExceededReason} />
        </HaltedMessageText>
      );
    } else if (reason.isAgenticProviderNotEnabledReason) {
      const providerName = getAgentProviderDisplayName(
        haltedStep?.agentProvider ?? 'Agent'
      );
      return (
        <>
          <HaltedMessageText>
            <FormattedMessage
              {...messages.agenticPipelinesProviderNotEnabledReason}
              values={{ providerName }}
            />
          </HaltedMessageText>
          <HaltedMessageLinks>
            <ExternalLink
              href={getAgenticPipelinesPageUrl(repository.getWorkplaceSlug())}
            >
              <FormattedMessage
                {...messages.enableProviderAgenticPipelines}
                values={{ providerName }}
              />
            </ExternalLink>
          </HaltedMessageLinks>
        </>
      );
    } else {
      return (
        <HaltedMessageText>
          <FormattedMessage
            {...messages.automaticalPausedMessage}
            values={{
              reason: reason === undefined,
              isDeploymentConcurrencyReason:
                reason.isDeploymentConcurrencyReason,
            }}
          />
        </HaltedMessageText>
      );
    }
  }, [
    checks.deploymentCheckSuites,
    checks.jsdGatingCheckRuns,
    deploymentEnvironment,
    deployments,
    getReasonUrl,
    haltedStep?.isManual,
    haltedStep?.statusReason,
    haltedStep?.uuid,
    haltedStep?.agentProvider,
    pipeline.isBranchPipeline,
    pipeline.sourceBranch,
    repository,
  ]);

  const warningCardTitle = useMemo(() => {
    return (
      (haltedStep?.statusReason as HaltedReason)?.warningText ||
      pipeline.statusText
    );
  }, [haltedStep, pipeline.statusText]);

  return (
    <WarningCard title={warningCardTitle} color={pipeline.statusColor}>
      <HaltedMessageWrapper>{reasonMessage}</HaltedMessageWrapper>
    </WarningCard>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(HaltedMessage);
